import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routesConfig from 'constants/routesConfig.json';

// components
import ApiLoading from 'shared/ApiLoading';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import { TopBar } from 'topBar/components/TopBar';
import TeamPageView from 'teamProfiles/pages/TeamPage';
import SearchModal from 'teamProfiles/components/SearchModal';
import { General } from 'icons/untitledui';

// hooks
import { useProfiles } from 'teamProfiles/hooks/teamApi';
import useWindowSize from 'shared/hooks/useWindowResize';
import { useScrolledPast } from 'topBar/hooks/useScrolledPast';
import breakpoints from 'theme/atoms/breakpoints';

// styles
import * as PageLayout from 'layout/styles/PageLayout.style';
import TeamEmptyState from 'teamProfiles/components/TeamEmptyState';
import { getFirstName } from 'teamProfiles/helpers/profileUtils';

export default function TeamPageIndex(): JSX.Element {
  const history = useHistory();
  const { t } = useTranslation('pageTeam');
  const { t: tSidePanel } = useTranslation('sidePanel');
  const { data: profiles, isLoading } = useProfiles();

  const sortedProfiles = useMemo(() => {
    if (!profiles) return [];
    return [...profiles].sort((a, b) => {
      const aName = getFirstName(a.full_name) || a.full_name;
      const bName = getFirstName(b.full_name) || b.full_name;
      return aName.localeCompare(bName);
    });
  }, [profiles]);

  const windowSize = useWindowSize();
  const isDesktopView = windowSize[0] > parseInt(breakpoints.tablet, 10);
  const titleRef = useRef<HTMLDivElement>(null);
  const isScrolledPast = useScrolledPast(titleRef);

  // Set default profile when in desktop view - redirect to first profile
  useEffect(() => {
    if (isDesktopView && sortedProfiles && sortedProfiles.length > 0) {
      const firstProfile = sortedProfiles[0];
      history.replace(`${routesConfig.TEAM_PAGE}/${firstProfile.id}`);
    }
  }, [isDesktopView, sortedProfiles, history]);

  const handleProfileClick = (profileId: string) => {
    if (isDesktopView) {
      history.replace(`${routesConfig.TEAM_PAGE}/${profileId}`);
    } else {
      history.push(`${routesConfig.TEAM_PAGE}/${profileId}`);
    }
  };

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const handleAddProfile = () => {
    history.push(`${routesConfig.TEAM_PAGE}/new`);
  };

  if (isLoading) return <ApiLoading />;

  // In desktop view, redirect to first profile, so this should rarely render
  // But keep it for the transition period
  if (isDesktopView && profiles && profiles.length > 0) {
    return <ApiLoading />; // Will redirect in useEffect
  }

  if (isDesktopView && (!profiles || profiles.length === 0))
    return (
      <ErrorBoundary>
        <PageLayout.PageWithSubNav>
          <PageLayout.ColumnSubNav>
            <TeamPageView
              profiles={sortedProfiles}
              onProfileClick={handleProfileClick}
              isDesktopView={isDesktopView}
              selectedProfileId={null}
              onAddProfileClick={handleAddProfile}
            />
          </PageLayout.ColumnSubNav>
          <PageLayout.ContentContainer>
            <PageLayout.Content>
              <TeamEmptyState />
            </PageLayout.Content>
          </PageLayout.ContentContainer>
        </PageLayout.PageWithSubNav>
      </ErrorBoundary>
    );

  // Mobile view - show list only
  return (
    <ErrorBoundary>
      <PageLayout.Page>
        <PageLayout.ContentContainer>
          <TopBar
            iconActions={[
              {
                icon: <General.SearchSm size={20} />,
                onClick: () => setIsSearchModalOpen(true),
                ariaLabel: t('viewAllProfiles.searchPlaceholder'),
              },
            ]}
            menuItems={[
              {
                label: t('teamPage.addTeamProfile'),
                onClick: handleAddProfile,
                icon: <General.Plus size={22} />,
              },
            ]}
            showBackButton={!isDesktopView}
            showOnDesktop={false}
            backButtonAriaLabel={tSidePanel('Menu')}
            title={t('viewAllProfiles.team')}
            showTitle={isScrolledPast}
          />
          <PageLayout.Content>
            <TeamPageView
              profiles={sortedProfiles}
              onProfileClick={handleProfileClick}
              isDesktopView={isDesktopView}
              selectedProfileId={null}
              onAddProfileClick={handleAddProfile}
              titleRef={titleRef}
            />
          </PageLayout.Content>
        </PageLayout.ContentContainer>
      </PageLayout.Page>
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        profiles={sortedProfiles}
        onProfileClick={handleProfileClick}
      />
    </ErrorBoundary>
  );
}
